
import MeetHourJS from '../lib-meet-hour';
import { toState } from '../redux';

export * from './functions.any';

/**
 * Returns the deviceId for the currently used camera.
 *
 * @param {Object} state - The state of the application.
 * @returns {string}
 */
export function getCurrentCameraDeviceId(state: Object) {
    const [ currentTrackDeviceId ] = (state['features/base/tracks'] || [])
        .map(t => t.mhTrack)
        .filter(t => t && t.isLocal() && t.isVideoTrack() && (t.getVideoType ? t.getVideoType() === 'camera' : t.videoType === 'camera'))
        .map(t => t.getDeviceId());

    if (currentTrackDeviceId) {
        return currentTrackDeviceId;
    }

    const userSelected = getUserSelectedCameraDeviceId(state);

    if (userSelected) {
        return userSelected;
    }

    const settings = state['features/base/settings'];

    if (settings?.userSelectedCameraDeviceId) {
        return settings.userSelectedCameraDeviceId;
    }

    if (settings?.cameraDeviceId) {
        return settings.cameraDeviceId;
    }

    const availableInputs = state['features/base/devices']?.availableDevices?.videoInput || [];

    if (availableInputs.length > 0) {
        const defaultInput = availableInputs.find(d => d.deviceId === 'default');

        return defaultInput ? defaultInput.deviceId : availableInputs[0].deviceId;
    }

    return '';
}

/**
 * Returns the deviceId for the currently used microphone.
 *
 * @param {Object} state - The state of the application.
 * @returns {string}
 */
export function getCurrentMicDeviceId(state: Object) {
    const currentTrackDeviceId = getDeviceIdByType(state, 'isAudioTrack');

    if (currentTrackDeviceId) {
        return currentTrackDeviceId;
    }

    const userSelected = getUserSelectedMicDeviceId(state);

    if (userSelected) {
        return userSelected;
    }

    const settings = state['features/base/settings'];

    if (settings?.userSelectedMicDeviceId) {
        return settings.userSelectedMicDeviceId;
    }

    if (settings?.micDeviceId) {
        return settings.micDeviceId;
    }

    const availableInputs = state['features/base/devices']?.availableDevices?.audioInput || [];

    if (availableInputs.length > 0) {
        const defaultInput = availableInputs.find(d => d.deviceId === 'default');

        return defaultInput ? defaultInput.deviceId : availableInputs[0].deviceId;
    }

    return 'default';
}

/**
 * Returns the deviceId for the currently used speaker.
 *
 * @param {Object} state - The state of the application.
 * @returns {string}
 */
export function getCurrentOutputDeviceId(state: Object) {
    const settings = state['features/base/settings'];
    const currentId = settings?.audioOutputDeviceId;

    if (currentId) {
        return currentId;
    }

    const userSelected = getUserSelectedOutputDeviceId(state);

    if (userSelected) {
        return userSelected;
    }

    if (settings?.userSelectedAudioOutputDeviceId) {
        return settings.userSelectedAudioOutputDeviceId;
    }

    const availableOutputs = state['features/base/devices']?.availableDevices?.audioOutput || [];

    if (availableOutputs.length > 0) {
        const defaultOutput = availableOutputs.find(d => d.deviceId === 'default');

        return defaultOutput ? defaultOutput.deviceId : availableOutputs[0].deviceId;
    }

    return MeetHourJS?.mediaDevices?.getAudioOutputDevice?.() || 'default';
}

/**
 * Returns the deviceId for the corresponding local track type.
 *
 * @param {Object} state - The state of the application.
 * @param {string} isType - Can be 'isVideoTrack' | 'isAudioTrack'.
 * @returns {string}
 */
function getDeviceIdByType(state: Object, isType: string) {
    const tracks = state['features/base/tracks'] || [];

    if (isType === 'isVideoTrack') {
        const cameraTrack = tracks.find(t =>
            t
            && t.local
            && t.mhTrack
            && t.videoType !== 'desktop'
            && t.mediaType !== 'screenshare'
            && t.mhTrack.getVideoType?.() !== 'desktop'
            && (t.mhTrack.isVideoTrack?.() || t.mediaType === 'video')
        );

        return cameraTrack?.mhTrack?.getDeviceId() || '';
    }

    const [ deviceId ] = tracks
        .filter(t => t && t.local && t.mhTrack && t.mhTrack[isType]())
        .map(t => t.mhTrack.getDeviceId());

    return deviceId || '';
}

/**
 * Searches known devices for a matching deviceId and fall back to matching on
 * label. Returns the stored preferred cameraDeviceId if a match is not found.
 *
 * @param {Object|Function} stateful - The redux state object or
 * {@code getState} function.
 * @returns {string}
 */
export function getUserSelectedCameraDeviceId(stateful) {
    const state = toState(stateful);
    const {
        userSelectedCameraDeviceId,
        userSelectedCameraDeviceLabel
    } = state['features/base/settings'];
    const { videoInput } = state['features/base/devices'].availableDevices;

    return _getUserSelectedDeviceId({
        availableDevices: videoInput,

        // Operating systems may append " #{number}" somewhere in the label so
        // find and strip that bit.
        matchRegex: /\s#\d*(?!.*\s#\d*)/,
        userSelectedDeviceId: userSelectedCameraDeviceId,
        userSelectedDeviceLabel: userSelectedCameraDeviceLabel,
        replacement: ''
    });
}

/**
 * Searches known devices for a matching deviceId and fall back to matching on
 * label. Returns the stored preferred micDeviceId if a match is not found.
 *
 * @param {Object|Function} stateful - The redux state object or
 * {@code getState} function.
 * @returns {string}
 */
export function getUserSelectedMicDeviceId(stateful) {
    const state = toState(stateful);
    const {
        userSelectedMicDeviceId,
        userSelectedMicDeviceLabel
    } = state['features/base/settings'];
    const { audioInput } = state['features/base/devices'].availableDevices;

    return _getUserSelectedDeviceId({
        availableDevices: audioInput,

        // Operating systems may append " ({number}-" somewhere in the label so
        // find and strip that bit.
        matchRegex: /\s\(\d*-\s(?!.*\s\(\d*-\s)/,
        userSelectedDeviceId: userSelectedMicDeviceId,
        userSelectedDeviceLabel: userSelectedMicDeviceLabel,
        replacement: ' ('
    });
}

/**
 * Searches known devices for a matching deviceId and falls back to matching on
 * label. Returns the stored preferred audioOutputDeviceId if a match is not found.
 *
 * @param {Object|Function} stateful - The redux state object or
 * {@code getState} function.
 * @returns {string}
 */
export function getUserSelectedOutputDeviceId(stateful) {
    const state = toState(stateful);
    const {
        userSelectedAudioOutputDeviceId,
        userSelectedAudioOutputDeviceLabel
    } = state['features/base/settings'];
    const { audioOutput } = state['features/base/devices'].availableDevices;

    return _getUserSelectedDeviceId({
        availableDevices: audioOutput,
        matchRegex: undefined,
        userSelectedDeviceId: userSelectedAudioOutputDeviceId,
        userSelectedDeviceLabel: userSelectedAudioOutputDeviceLabel,
        replacement: undefined
    });
}

/**
 * Returns the saved display name.
 *
 * @param {Object} state - The state of the application.
 * @returns {string}
 */
export function getDisplayName(state: Object): string {
    return state['features/base/settings'].displayName || '';
}

/**
 * Handles changes to the `disableCallIntegration` setting.
 * Noop on web.
 *
 * @param {boolean} disabled - Whether call integration is disabled or not.
 * @returns {void}
 */
export function handleCallIntegrationChange() { /* noop */ }

/**
 * Handles changes to the `disableCrashReporting` setting.
 * Noop on web.
 *
 * @param {boolean} disabled - Whether crash reporting is disabled or not.
 * @returns {void}
 */
export function handleCrashReportingChange() { /* noop */ }

/**
 * A helper function to abstract the logic for choosing which device ID to
 * use. Falls back to fuzzy matching on label if a device ID match is not found.
 *
 * @param {Object} options - The arguments used to match find the preferred
 * device ID from available devices.
 * @param {Array<string>} options.availableDevices - The array of currently
 * available devices to match against.
 * @param {Object} options.matchRegex - The regex to use to find strings
 * appended to the label by the operating system. The matches will be replaced
 * with options.replacement, with the intent of matching the same device that
 * might have a modified label.
 * @param {string} options.userSelectedDeviceId - The device ID the participant
 * prefers to use.
 * @param {string} options.userSelectedDeviceLabel - The label associated with the
 * device ID the participant prefers to use.
 * @param {string} options.replacement - The string to use with
 * options.matchRegex to remove identifies added to the label by the operating
 * system.
 * @private
 * @returns {string} The preferred device ID to use for media.
 */
function _getUserSelectedDeviceId(options) {
    const {
        availableDevices,
        matchRegex = '',
        userSelectedDeviceId,
        userSelectedDeviceLabel,
        replacement = ''
    } = options;

    // If there is no label at all, there is no need to fall back to checking
    // the label for a fuzzy match.
    if (!userSelectedDeviceLabel || !userSelectedDeviceId) {
        return userSelectedDeviceId;
    }

    const foundMatchingBasedonDeviceId = availableDevices?.find(
        candidate => candidate.deviceId === userSelectedDeviceId);

    // Prioritize matching the deviceId
    if (foundMatchingBasedonDeviceId) {
        return userSelectedDeviceId;
    }

    const strippedDeviceLabel
        = matchRegex ? userSelectedDeviceLabel.replace(matchRegex, replacement)
            : userSelectedDeviceLabel;
    const foundMatchBasedOnLabel = availableDevices?.find(candidate => {
        const { label } = candidate;

        if (!label) {
            return false;
        } else if (strippedDeviceLabel === label) {
            return true;
        }

        const strippedCandidateLabel
            = label.replace(matchRegex, replacement);

        return strippedDeviceLabel === strippedCandidateLabel;
    });

    return foundMatchBasedOnLabel ? foundMatchBasedOnLabel.deviceId : userSelectedDeviceId;
}

/**
 * Should we hide the helper dialog when a user tries to do audio only screen sharing.
 *
 * @param {Object} state - The state of the application.
 * @returns {boolean}
 */
export function shouldHideShareAudioHelper(state) {
    return state['features/base/settings'].hideShareAudioHelper;
}

/**
 * Should we hide the helper dialog when a user tries to do normal screen sharing.
 *
 * @param {Object} state - The state of the application.
 * @returns {boolean}
 */
export function shouldHideShareScreenHelper(state) {
    return state['features/base/settings'].hideShareScreenHelper;
}
